import { createSelector } from '@reduxjs/toolkit';

import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { getTransactionsOfTypes, txnTypesCashAndRefund } from 'app-components/flux/transactions/transactionsSelectors';
import {
  getReceiptAttachments,
  TRANSACTIONS_REDUCER_KEY,
  type TransactionStore,
} from 'app-components/flux/transactions/transactionsTypes';

import { createReceiptCenterQueries } from './receiptCenterQueries';
import { isReceiptLinkTransactionCandidate } from './receiptMatch';
import {
  isAttachingToTransaction,
  isLocalReceipt,
  isProcessingOrErrorReceipt,
  isProcessingReceipt,
} from './receiptsPredicates';
import { receiptsAdapter } from './receiptsSlice';
import {
  type FetchReceiptsParams,
  type LocalLinkChange,
  RECEIPTS_REDUCER_KEY,
  type Receipt,
  type ReceiptCenterQueries,
  type ReceiptPageQuery,
  type ReceiptReviewState,
  type ReceiptSummary,
  type ReceiptsState,
} from './receiptsTypes';

type StateWithReceipts = { [RECEIPTS_REDUCER_KEY]: ReceiptsState };
type ReceiptCenterAccessState = Parameters<typeof entitlementsSelectors.getTopTierEntitlement>[0] &
  Parameters<typeof featureFlagsSelectors.getFeatureFlag>[0];
type ReceiptLinkTransactionsState = Parameters<typeof getTransactionsOfTypes>[0];
type ReceiptLinkTransactions = ReturnType<typeof getTransactionsOfTypes>;
type TransactionResourcesState = { [TRANSACTIONS_REDUCER_KEY]: TransactionStore };
type ReceiptDataSyncState = ReceiptCenterAccessState & StateWithReceipts & TransactionResourcesState;

export type ReceiptSortField = 'uploadedDate' | 'receiptDate';

const getSlice = (state: StateWithReceipts): ReceiptsState => state[RECEIPTS_REDUCER_KEY];

const adapterSelectors = receiptsAdapter.getSelectors<StateWithReceipts>(getSlice);

const isVisibleReceipt = (receipt: Receipt): boolean => !receipt.isDeleted;

const getValidTimeValue = (date: string): number | null => {
  const parsedDate = Date.parse(date);
  return Number.isNaN(parsedDate) ? null : parsedDate;
};

const getTimeValue = (date: string): number => getValidTimeValue(date) ?? 0;

const getReceiptDateTimeValue = (receipt: Receipt): number => {
  const transactionDate = receipt.extraction?.transactionDate;

  if (transactionDate) {
    const transactionTimeValue = getValidTimeValue(transactionDate);

    if (transactionTimeValue !== null) {
      return transactionTimeValue;
    }
  }

  return getTimeValue(receipt.createdDate);
};

export const sortReceiptsBy = (receipts: ReadonlyArray<Receipt>, sortField: ReceiptSortField): Receipt[] =>
  receipts.slice().sort((a, b) => {
    const uploadedDateDifference = getTimeValue(b.createdDate) - getTimeValue(a.createdDate);

    if (sortField === 'uploadedDate') {
      return uploadedDateDifference || a.id.localeCompare(b.id);
    }

    return (
      getReceiptDateTimeValue(b) - getReceiptDateTimeValue(a) || uploadedDateDifference || a.id.localeCompare(b.id)
    );
  });

const getProcessingReceiptBatchTime = (receipt: Receipt): number =>
  receipt.localUploadOrder?.batchCreatedAt ?? getTimeValue(receipt.createdDate);

export const sortProcessingReceiptsByUploadOrder = (receipts: ReadonlyArray<Receipt>): Receipt[] =>
  receipts
    .slice()
    .sort(
      (first, second) =>
        getProcessingReceiptBatchTime(second) - getProcessingReceiptBatchTime(first) ||
        (first.localUploadOrder?.batchIndex ?? 0) - (second.localUploadOrder?.batchIndex ?? 0) ||
        first.id.localeCompare(second.id),
    );

const selectRawReceipts = (state: StateWithReceipts): Receipt[] => adapterSelectors.selectAll(state);

export const selectReceiptCenterEnabled: (state: ReceiptCenterAccessState) => boolean = createSelector(
  [
    (state: ReceiptCenterAccessState): boolean =>
      Boolean(featureFlagsSelectors.getFeatureFlag(state, 'receiptsCaptureRelease')),
    (state: ReceiptCenterAccessState) => entitlementsSelectors.getTopTierEntitlement(state),
  ],
  (receiptsCaptureEnabled, topTierEntitlement): boolean =>
    receiptsCaptureEnabled && topTierEntitlement?.id === 'ACME_HAB',
);

/** The transaction entry point requires both its release flag and existing Receipt Center access. */
export const selectTransactionReceiptUploadEnabled = (state: ReceiptCenterAccessState): boolean =>
  Boolean(featureFlagsSelectors.getFeatureFlag(state, 'receiptsTransactionUploadRelease')) &&
  selectReceiptCenterEnabled(state);

/**
 * Receipt data may continue syncing for users who already have receipt data or receipt-backed
 * transactions, even after creation access is disabled. Users with no receipt history do not make
 * their first receipt-service request until Receipt Center access is enabled.
 */
export const selectShouldSyncReceiptData: (state: ReceiptDataSyncState) => boolean = createSelector(
  [
    selectReceiptCenterEnabled,
    selectRawReceipts,
    (state: TransactionResourcesState) => state[TRANSACTIONS_REDUCER_KEY].resourcesById,
  ],
  (receiptCenterEnabled, receipts, transactions): boolean =>
    receiptCenterEnabled ||
    receipts.length > 0 ||
    transactions.valueSeq().some((transaction) => getReceiptAttachments(transaction.attachments).length > 0),
);

export const selectAllReceipts: (state: StateWithReceipts) => Receipt[] = createSelector(
  [selectRawReceipts],
  (receipts: Receipt[]): Receipt[] => sortReceiptsBy(receipts.filter(isVisibleReceipt), 'uploadedDate'),
);

export const selectReceiptIds: (state: StateWithReceipts) => string[] = createSelector(
  [selectAllReceipts],
  (receipts: Receipt[]): string[] => receipts.map((receipt) => receipt.id),
);

export const selectReceiptById = (state: StateWithReceipts, id: string): Receipt | undefined => {
  const receipt = adapterSelectors.selectById(state, id);
  return receipt && isVisibleReceipt(receipt) ? receipt : undefined;
};

export const selectReceiptByTransactionId = (state: StateWithReceipts, transactionId: string): Receipt | undefined =>
  selectRawReceipts(state).find(
    (receipt: Receipt): boolean =>
      isVisibleReceipt(receipt) && receipt.transactionId === transactionId && !receipt.targetTransactionId,
  );

/** Pending transaction-originated work is kept separate from a confirmed receipt link. */
export const selectTargetedReceiptByTransactionId = (
  state: StateWithReceipts,
  transactionId: string,
): Receipt | undefined =>
  selectRawReceipts(state).find(
    (receipt: Receipt): boolean => isVisibleReceipt(receipt) && receipt.targetTransactionId === transactionId,
  );

export const selectLocalReceiptLinkChanges = (state: StateWithReceipts): Readonly<Record<string, LocalLinkChange>> =>
  state[RECEIPTS_REDUCER_KEY].localLinkChanges;

export const selectLocalReceiptLinkVersion = (state: StateWithReceipts): number =>
  state[RECEIPTS_REDUCER_KEY].localLinkVersion;

/** Receipts this device detached from the transaction or deleted, once the store no longer links them to it. */
export const selectReceiptsRemovedFromTransaction = (
  state: StateWithReceipts,
  transactionId: string,
): Array<{ id: string; documentId?: string }> =>
  Object.entries(selectLocalReceiptLinkChanges(state)).flatMap(([id, change]) => {
    if (change.transactionId === transactionId) return [];
    const receipt = state[RECEIPTS_REDUCER_KEY].entities[id];
    if (receipt && (receipt.transactionId === transactionId || receipt.targetTransactionId === transactionId)) {
      return [];
    }
    return [{ id, documentId: receipt?.documentId ?? change.documentId }];
  });

export const selectReceiptLinkTransactions: (state: ReceiptLinkTransactionsState) => ReceiptLinkTransactions =
  createSelector(
    [(state: ReceiptLinkTransactionsState) => getTransactionsOfTypes(state, txnTypesCashAndRefund)],
    (transactions: ReceiptLinkTransactions): ReceiptLinkTransactions =>
      transactions.filter((transaction) => isReceiptLinkTransactionCandidate(transaction)),
  );

export const selectLastSyncDate = (state: StateWithReceipts): string | null => getSlice(state).lastSyncDate;

export const selectIsLoading = (state: StateWithReceipts): boolean => getSlice(state).isLoading;

/**
 * True while the very first receipts fetch is still outstanding and nothing can be shown yet:
 * no receipts in the store (fetched, merged, or locally created) and no failed fetch to fall
 * back on. UIs show a loading state instead of an empty/intro view while this is true.
 */
export const selectIsInitialLoad = (state: StateWithReceipts): boolean => {
  const slice = getSlice(state);
  return slice.loadPending && slice.error == null && slice.ids.length === 0;
};

export const selectForReview: (state: StateWithReceipts) => Receipt[] = createSelector(
  [selectAllReceipts],
  (receipts: Receipt[]): Receipt[] =>
    receipts.filter((receipt) => !receipt.transactionId || !!receipt.targetTransactionId),
);

export const selectReviewed: (state: StateWithReceipts) => Receipt[] = createSelector(
  [selectAllReceipts],
  (receipts: Receipt[]): Receipt[] =>
    receipts.filter((receipt) => Boolean(receipt.transactionId && !receipt.targetTransactionId)),
);

export const selectHasSortableReceipts: (state: StateWithReceipts) => boolean = createSelector(
  [selectForReview, selectReviewed],
  (forReview, reviewed): boolean =>
    reviewed.length > 0 || forReview.some((receipt) => !isProcessingOrErrorReceipt(receipt)),
);

export const selectHasProcessingReceipts: (state: StateWithReceipts) => boolean = createSelector(
  [selectAllReceipts],
  (receipts: Receipt[]): boolean => receipts.some(isProcessingReceipt),
);

export const selectNeedsReconciliation: (state: StateWithReceipts) => boolean = createSelector(
  [selectAllReceipts],
  (receipts: Receipt[]): boolean => receipts.some((receipt) => receipt.needsReconciliation === true),
);

const getOldestInFlightModifiedDate = (receipts: ReadonlyArray<Receipt>): string | null =>
  receipts.reduce<string | null>((oldestDate, receipt) => {
    if (!isProcessingReceipt(receipt)) {
      return oldestDate;
    }

    return !oldestDate || getTimeValue(receipt.modifiedDate) < getTimeValue(oldestDate)
      ? receipt.modifiedDate
      : oldestDate;
  }, null);

/**
 * Returns the incremental list cursor consumers should use for receipt polling. While any receipt
 * is in flight, retain an overlap back to the oldest active receipt so a newer create/Bump response
 * cannot advance the collection cursor past an extraction update the client has not received yet.
 */
export const selectReceiptSyncParams: (state: StateWithReceipts) => FetchReceiptsParams | undefined = createSelector(
  [selectLastSyncDate, selectNeedsReconciliation, selectAllReceipts],
  (lastSyncDate, needsReconciliation, receipts): FetchReceiptsParams | undefined => {
    if (needsReconciliation || !lastSyncDate) {
      return undefined;
    }

    const oldestInFlightModifiedDate = getOldestInFlightModifiedDate(receipts);
    return {
      modifiedAfter:
        oldestInFlightModifiedDate && getTimeValue(oldestInFlightModifiedDate) < getTimeValue(lastSyncDate)
          ? oldestInFlightModifiedDate
          : lastSyncDate,
    };
  },
);

const emptyCenterQueries = createReceiptCenterQueries();
const emptyReceiptIds: string[] = [];
const selectSummaryPendingReceiptIds = (state: StateWithReceipts): string[] =>
  selectReceiptCenterQueries(state).summaryPendingReceiptIds ?? emptyReceiptIds;

export const selectReceiptCenterQueries = (state: StateWithReceipts): ReceiptCenterQueries =>
  getSlice(state).center ?? emptyCenterQueries;

export const selectReceiptPageQuery = (state: StateWithReceipts, reviewState: ReceiptReviewState): ReceiptPageQuery =>
  selectReceiptCenterQueries(state).pages[reviewState];

export const selectReceiptPageRows: (state: StateWithReceipts, reviewState: ReceiptReviewState) => Receipt[] =
  createSelector(
    [
      (state: StateWithReceipts, reviewState: ReceiptReviewState): string[] =>
        selectReceiptPageQuery(state, reviewState).ids,
      (state: StateWithReceipts): ReceiptsState['entities'] => getSlice(state).entities,
    ],
    (ids: string[], entities: ReceiptsState['entities']): Receipt[] =>
      ids.map((id) => entities[id]).filter((receipt): receipt is Receipt => Boolean(receipt && !receipt.isDeleted)),
  );

/** Add local uploads and recently registered rows without duplicating a document from the server page. */
export const selectReceiptCenterProcessingRows: (state: StateWithReceipts) => Receipt[] = createSelector(
  [
    selectRawReceipts,
    (state: StateWithReceipts): string[] => selectReceiptPageQuery(state, 'PROCESSING_OR_ERROR').ids,
    selectSummaryPendingReceiptIds,
  ],
  (receipts: Receipt[], pageIds: string[], pendingIds: string[]): Receipt[] => {
    const ids = new Set([...pageIds, ...pendingIds]);
    return sortProcessingReceiptsByUploadOrder(
      receipts.filter(
        (receipt) =>
          !receipt.isDeleted &&
          !receipt.transactionId &&
          isProcessingOrErrorReceipt(receipt) &&
          (ids.has(receipt.id) || isLocalReceipt(receipt) || Boolean(receipt.localUploadOrder)),
      ),
    );
  },
);

export const selectReceiptCenterProcessingCounts: (
  state: StateWithReceipts,
) => Pick<ReceiptSummary, 'processing' | 'processingErrors'> = createSelector(
  [
    (state: StateWithReceipts): ReceiptSummary | null => selectReceiptCenterQueries(state).summary,
    selectSummaryPendingReceiptIds,
    selectReceiptCenterProcessingRows,
  ],
  (summary, pendingIds, receipts): Pick<ReceiptSummary, 'processing' | 'processingErrors'> => {
    const pending = new Set(pendingIds);
    return receipts.reduce(
      (counts, receipt) => {
        if (isLocalReceipt(receipt) || pending.has(receipt.id)) {
          // A receipt still attaching to its transaction is in progress, not an error.
          if (isProcessingReceipt(receipt) || isAttachingToTransaction(receipt)) counts.processing += 1;
          else counts.processingErrors += 1;
        }
        return counts;
      },
      { processing: summary?.processing ?? 0, processingErrors: summary?.processingErrors ?? 0 },
    );
  },
);
